import React, { useCallback, useEffect, useState, useRef, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { BeforeAfterTabs } from '../components/BeforeAfterTabs';
import { ImageCompareSlider } from '../components/ImageCompareSlider';
interface CheckoutCaseStudyPageProps {
  onBack: () => void;
  onNext?: () => void;
  nextLabel?: string;
}
const sections = [{
  id: 'problem',
  label: 'The Problem'
}, {
  id: 'impact',
  label: 'Impact'
}, {
  id: 'solution',
  label: 'The Solution'
}, {
  id: 'understanding',
  label: 'Understanding'
}, {
  id: 'modular-solution',
  label: 'Modular Design'
}, {
  id: 'design-principles',
  label: 'Design Principles'
}, {
  id: 'building-for-scale',
  label: 'Building for Scale'
}, {
  id: 'consistency',
  label: 'Brand Theming'
}, {
  id: 'key-learnings',
  label: 'Key Learnings'
}];
export function CheckoutCaseStudyPage({
  onBack,
  onNext,
  nextLabel = 'Design System'
}: CheckoutCaseStudyPageProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isScrollingRef = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.round(scrollTop / docHeight * 100) : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (isScrollingRef.current) return;
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = sections.findIndex(s => s.id === entry.target.id);
          if (idx !== -1) setActiveIndex(idx);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, sections.length - 1));
    const el = document.getElementById(sections[clamped].id);
    if (el) {
      isScrollingRef.current = true;
      setActiveIndex(clamped);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, []);
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <div className="min-h-screen bg-[#F8F6F3] text-[#6B6560] selection:bg-[#1A1A1A]/10 selection:text-[#1A1A1A]" data-id="element-549">
      <div className="max-w-5xl mx-auto px-8 md:px-16 py-12 md:py-20 pb-32" data-id="element-550">
        {/* Back Navigation */}
        <motion.button onClick={() => {
        onBack();
        setTimeout(() => {
          document.getElementById('work')?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }} initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="group flex items-center gap-2 text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] mb-12 transition-colors" data-id="element-551">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" data-id="element-552" />
          Back to Work
        </motion.button>

        {/* Hero Section */}
        <motion.section initial="hidden" animate="visible" variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-553">
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 leading-tight" data-id="element-554">
            Redesigning the checkout experience
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-[#6B6560] max-w-3xl leading-relaxed mb-12" data-id="element-555">
            Within Loblaw Digital, each primary e-comm business (Grocery,
            Shoppers Drug Mart & Joe Fresh) had created a checkout experience
            which had increasingly diverged over time. Customers experienced an
            inconsistent checkout, while design and development teams duplicated
            effort building similar solutions independently.
          </motion.p>

          <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#E8E4DF] pt-8 mb-16" data-id="element-556">
            <div data-id="element-557">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-558">
                Timeframe
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-559">2025</p>
            </div>
            <div data-id="element-560">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-561">
                Roles
              </h3>
              <div className="space-y-1" data-id="element-562">
                <p className="text-[#1A1A1A]" data-id="element-563">Lead Designer</p>

                <p className="text-[#1A1A1A]" data-id="element-564">UX Researcher</p>
              </div>
            </div>
            <div data-id="element-565">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-566">
                Tools
              </h3>
              <div className="space-y-1" data-id="element-567">
                <p className="text-[#1A1A1A]" data-id="element-568">Figma</p>
                <p className="text-[#1A1A1A]" data-id="element-569">Storybook</p>
                <p className="text-[#1A1A1A]" data-id="element-570">Tokens Studio</p>
                <p className="text-[#1A1A1A]" data-id="element-571">GitHub</p>
                <p className="text-[#1A1A1A]" data-id="element-572">Contentful</p>
              </div>
            </div>
            <div data-id="element-573">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-574">
                Design Team Size
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-575">2</p>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full bg-[#E8E4DF] rounded-2xl overflow-hidden" data-id="element-576">
            <video src="https://cdn.magicpatterns.com/uploads/g1dxwFcdxkwnH3hhSxWoHi/showcase.mp4" autoPlay loop muted playsInline className="w-full h-auto block" data-id="element-577" />
          </motion.div>
        </motion.section>

        {/* The Problem */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32 scroll-mt-8" data-id="element-578">
          <motion.h2 id="problem" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-579">
            The Problem
          </motion.h2>
          <motion.div variants={fadeInUp} className="text-lg leading-relaxed text-[#6B6560] max-w-3xl" data-id="element-580">
            <p data-id="element-581">
              Within Loblaw Digital, each primary e-comm business (Grocery,
              Shoppers Drug Mart & Joe Fresh) had created a checkout experience
              which had increasingly diverged over time. Customers experienced
              an inconsistent checkout, while design and development teams
              duplicated effort building similar solutions independently.
            </p>
          </motion.div>
        </motion.section>

        {/* Impact */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-582">
          <motion.h2 id="impact" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-583">
            Impact
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-584">
            {[{
            value: '+5%',
            label: 'Conversion'
          }, {
            value: '3 → 1',
            label: 'Codebase reduction'
          }, {
            value: '+$1 million',
            label: 'Annual revenue increase'
          }].map((metric, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl flex flex-col items-center justify-end text-center border border-[#E8E4DF] h-full" data-id="element-585">
                <span className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 block text-center w-full" data-id="element-586">
                  {metric.value}
                </span>
                <span className="text-sm text-[#A09A94] uppercase tracking-wider leading-relaxed mt-auto text-center w-full" data-id="element-587">
                  {metric.label}
                </span>
              </motion.div>)}
          </div>
        </motion.section>

        {/* The Solution */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-588">
          <motion.h2 id="solution" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-589">
            The Solution
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-590">
            Create one centralized web application that, with the help of
            tokens, could be themed for each brand and serve both web and app
            experiences. This would enable teams across the organization to
            launch new features for all businesses from one centralized code
            base while reducing redundancy.
          </motion.p>
          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden" data-id="element-591">
            <img src="https://cdn.magicpatterns.com/uploads/shpEZdFiVPmutLKCMfNGGu/Checkout_mobile-desktop.png" alt="Solution overview showing checkout on mobile and desktop" className="w-full h-auto block" data-id="element-592" />
          </motion.div>
        </motion.section>

        {/* Understanding the Problem */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-593">
          <motion.h2 id="understanding" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-594">
            Understanding the Problem
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-595">
            I conducted a comprehensive audit of all three checkout experiences,
            analyzing divergences, discovering known customer pain points &
            planning future features. Key findings revealed:
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-596">
            <motion.div variants={fadeInUp} data-id="element-597">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-598">
                User Pain Points
              </h3>
              <ul className="space-y-4 text-[#6B6560]" data-id="element-599">
                {['Multiple steps created a laborious checkout', "The experience wasn't optimized for mobile", 'Errors lacked clear recovery paths', 'Information around costs, like shipping, were inconsistently shown'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" data-id="element-601" />
                    <span data-id="element-602">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-603">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-604">
                Business Challenges
              </h3>
              <ul className="space-y-4 text-[#6B6560]" data-id="element-605">
                {['Compliance updates required triple the implementation effort', 'Engineering teams were building the same features', 'Design teams were creating different solutions to the same UX problems', "Popular payment options, like Apple and Google Pay weren't available"].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-606">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" data-id="element-607" />
                    <span data-id="element-608">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="bg-white p-6 rounded-xl mb-16 border border-[#E8E4DF]" data-id="element-609">
            <p className="text-[#1A1A1A] text-sm leading-relaxed" data-id="element-610">
              <strong className="text-[#1A1A1A]" data-id="element-611">Core Insight:</strong>{' '}
              <span className="text-[#6B6560]" data-id="element-612">
                Users didn't care about our three brands, they wanted a fast,
                one-click, trustworthy way to complete their purchase. Internal
                teams needed flexible building blocks, not rigid templates.
              </span>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden" data-id="element-613">
            <BeforeAfterTabs data-id="element-614" />
          </motion.div>
        </motion.section>

        {/* The Solution: Modular by Design */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-615">
          <motion.h2 id="modular-solution" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-616">
            The Solution: Modular by Design
          </motion.h2>

          <motion.blockquote variants={fadeInUp} className="my-12 text-xl md:text-2xl text-[#1A1A1A] leading-relaxed max-w-3xl mx-auto text-center font-medium" data-id="element-617">
            Rather than designing three separate checkouts or forcing a
            one-size-fits-all solution, I created a system of reusable,
            composable components that could adapt to each brand's needs while
            maintaining consistent behavior and quality.
          </motion.blockquote>

          <motion.div variants={fadeInUp} className="mb-12" data-id="element-618">
            <div className="w-full aspect-video bg-[#E8E4DF] rounded-2xl overflow-hidden relative mb-4" data-id="element-619">
              <video src="https://cdn.magicpatterns.com/uploads/b2unfeBuCGzCQpwaJRPfeR/Modular-blocks(1).mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-620" />
            </div>
            <p className="text-sm text-[#A09A94] text-center leading-relaxed px-4" data-id="element-621">
              Every element of the checkout was designed as an independent,
              reusable component. This approach allowed engineering teams to
              compose checkout experiences like building blocks.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-622">
            {[{
            title: 'Address Input',
            desc: 'Address validation powered by a third party offered reliability & flexibility for everything from "buy online pickup in store" orders through Shoppers Drug Mart to grocery delivery through Loblaws.'
          }, {
            title: 'Payment Component',
            desc: 'Not only did we leverage popular payment methods like Google Pay and Apple Pay, we also integrated our own shared wallet service: PC Pay. This saved any payment methods within users One Account and enabled faster checkout.'
          }, {
            title: 'PC Optimum Redemption',
            desc: 'PC Optimum points are a beloved feature of the Loblaw ecosystem and needed to be visually prominent while functioning consistently no matter where you were shopping.'
          }, {
            title: 'Error Handling',
            desc: 'Consistent error messaging and giving clear steps to recovery were key to improving our checkout, which would previously often show error messages without providing customers with a remedy.'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-623">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-624">
                  {card.title}
                </h3>
                <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-625">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Design Principles */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-626">
          <motion.h2 id="design-principles" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-627">
            Design Principles
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-628">
            {[{
            title: 'One-click Checkout',
            desc: 'For customers who had already shopped with us and provided us with their information, we wanted to give them the opportunity to checkout with one click.'
          }, {
            title: 'Progressive Disclosure',
            desc: "Our checkout can contain a lot of complex information. Hiding ancillary information and defaulting to showing what's important helped distill the experience."
          }, {
            title: 'Brand Flexibility',
            desc: 'Just because we were using the same "building blocks" for the checkout powering all the brands, didn\'t mean the brands couldn\'t infuse their styling throughout. I ensured every token applied to tokens provided brands with that level of flexibility.'
          }, {
            title: 'Mobile-First',
            desc: "Our checkout web app would not only power the web experience, we'd also house the checkout within all of our mobile apps. That meant keeping the mobile experience top of mind at all times."
          }].map((principle, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-629">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-630">
                  {principle.title}
                </h3>
                <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-631">
                  {principle.desc}
                </p>
              </motion.div>)}
          </div>

          <motion.div variants={fadeInUp} className="w-full" data-id="element-632">
            <div className="w-full rounded-2xl overflow-hidden" data-id="element-633">
              <img src="https://cdn.magicpatterns.com/uploads/rD5pVKSnB7qNEyMNqf66N1/Specs.png" alt="Checkout on web and mobile" className="w-full h-auto block" data-id="element-634" />
            </div>
            <p className="text-sm text-[#A09A94] text-center leading-relaxed px-4 mt-4" data-id="element-635">
              An example of some of the thorough documentation we added that
              pointed to our web core design system files containing design
              tokens. Allowing devs to seamlessly implement our checkout designs
              across all breakpoints.
            </p>
          </motion.div>
        </motion.section>

        {/* Building for Scale */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-636">
          <motion.h2 id="building-for-scale" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-637">
            Building for Scale
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-638">
            Working closely with engineering, we established a component
            architecture that balanced flexibility with consistency. Each
            component was built as a self-contained module with:
          </motion.p>

          <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF] mb-12" data-id="element-639">
            <ul className="space-y-3 text-sm text-[#6B6560]" data-id="element-640">
              <li className="flex items-start gap-3" data-id="element-641">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A09A94] shrink-0" data-id="element-642" />
                <span data-id="element-643">
                  <strong className="text-[#1A1A1A]" data-id="element-644">Clear APIs</strong> for
                  passing data and handling state
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-645">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A09A94] shrink-0" data-id="element-646" />
                <span data-id="element-647">
                  <strong className="text-[#1A1A1A]" data-id="element-648">
                    Comprehensive error states
                  </strong>{' '}
                  and loading patterns
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-649">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A09A94] shrink-0" data-id="element-650" />
                <span data-id="element-651">
                  <strong className="text-[#1A1A1A]" data-id="element-652">Brand theming</strong>{' '}
                  using custom on-brand tokens
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-653">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A09A94] shrink-0" data-id="element-654" />
                <span data-id="element-655">
                  <strong className="text-[#1A1A1A]" data-id="element-656">Analytics hooks</strong>{' '}
                  for consistent tracking across implementations
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.p variants={fadeInUp} className="text-lg leading-relaxed max-w-3xl text-[#6B6560]" data-id="element-657">
            This modular approach meant that when regulations required adding
            new payment security features, we updated one component that
            propagated across all three brands automatically. When we wanted to
            test a new shipping option display, we could A/B test once and roll
            out the winner everywhere.
          </motion.p>
        </motion.section>

        {/* Consistency Across Brands */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-658">
          <motion.h2 id="consistency" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-659">
            Brand Theming
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-660">
            While each brand maintained its unique visual identity, the
            underlying behavior remained consistent. A user switching between
            our brands would always encounter familiar interaction patterns.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12" data-id="element-661">
            <motion.div variants={fadeInUp} data-id="element-662">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-4" data-id="element-663">
                Shoppers Drug Mart, Pickup
              </h3>
              <p className="text-sm text-[#6B6560]" data-id="element-664">
                We needed to communicate crucial information to customers that
                was only relevant for the "buy online, pickup in store" scenario
                that leveraged all our payment blocks.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} data-id="element-665">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-4" data-id="element-666">
                PC Express, Delivery
              </h3>
              <p className="text-sm text-[#6B6560]" data-id="element-667">
                The complexity of delivery orders could also be handled with our
                modular blocks while meeting business needs around tipping and
                timeslot selection.
              </p>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} data-id="element-668">
            <ImageCompareSlider leftImage="https://cdn.magicpatterns.com/uploads/bBJECysvPyDtrNNARPKSYJ/Left.png" rightImage="https://cdn.magicpatterns.com/uploads/ewResoXtQdaBYDbopPsTu7/Right.png" leftAlt="Loblaws Grocery Delivery checkout" rightAlt="Shoppers Drug Mart In-store pickup checkout" data-id="element-669" />
          </motion.div>
        </motion.section>

        {/* Key Learnings */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-670">
          <motion.h2 id="key-learnings" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-671">
            Key Learnings
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-672">
            {[{
            title: 'Start with Structure, Not Styling',
            desc: 'Early explorations focused too much on visual design. By shifting to interaction patterns and information architecture first, we created components that could adapt to any brand aesthetic while maintaining usability.'
          }, {
            title: 'Real Content Reveals Edge Cases',
            desc: 'Testing with actual product names, shipping addresses, and promo codes exposed assumptions in our designs. Personally validating many designs by translating strings of text to French was also crucial (and made me thankful for being bilingual).'
          }, {
            title: 'Documentation is Design Work',
            desc: 'Creating comprehensive component guidelines was as important as the designs themselves. Clear documentation ensured consistent implementation and empowered teams to compose checkout experiences confidently.'
          }, {
            title: 'Learn How Designs Will Be Implemented',
            desc: 'Taking the time to ask developers how they plan on implementing a design would have saved us a lot of time down the road. Early on, unbeknownst to design, devs decided to build many of the "building blocks" of the checkout as standalone components instead of re-usable "shells" into which you could place a variety of content. This approach required a massive overhaul of the components and could have been avoided.'
          }].map((learning, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-673">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-674">
                  {learning.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#6B6560]" data-id="element-675">
                  {learning.desc}
                </p>
              </motion.div>)}
          </div>

          <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF] mb-16" data-id="element-676">
            <p className="text-[#1A1A1A] text-sm leading-relaxed" data-id="element-677">
              <strong className="text-[#1A1A1A]" data-id="element-678">Most Important Lesson:</strong>{' '}
              <span className="text-[#6B6560]" data-id="element-679">
                Modularity doesn't mean complexity. The best component systems
                are simple building blocks that combine to solve complex
                problems.
              </span>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden" data-id="element-680">
            <img src="https://cdn.magicpatterns.com/uploads/wPPvkQBBsTdBB1De2q9d5W/Order_confirmation.png" alt="Order confirmation page showing checkout flow" className="w-full h-auto block" data-id="element-681" />
          </motion.div>
        </motion.section>

        {/* Bottom Project Navigation */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="border-t border-[#E8E4DF] pt-12 flex justify-between items-center" data-id="element-682">
          <button onClick={() => {
          onBack();
          setTimeout(() => {
            document.getElementById('work')?.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }} className="text-[#1A1A1A] hover:text-[#6B6560] transition-colors text-sm font-medium" data-id="element-683">
            ← Back to all work
          </button>
          <button onClick={onNext} className="group text-sm font-medium text-[#1A1A1A] hover:text-[#6B6560] transition-all duration-200 hover:translate-x-1" data-id="element-684">
            Next Project: {nextLabel} →
          </button>
        </motion.div>
      </div>

      {/* Fixed Section Navigator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="fixed bottom-20 md:bottom-8 left-0 right-0 md:left-16 lg:left-56 flex items-center justify-center gap-2 z-40 px-4" data-id="element-685">
        <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-[#E8E4DF] rounded-full px-2 py-2 shadow-lg shadow-black/5" data-id="element-686">
          <button onClick={() => scrollTo(activeIndex - 1)} disabled={activeIndex === 0} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Previous section" data-id="element-687">
            <ChevronLeft size={18} data-id="element-688" />
          </button>

          <AnimatePresence mode="wait" data-id="element-689">
            <motion.span key={activeIndex} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.15
          }} className="text-sm font-medium text-[#1A1A1A] w-[160px] text-center px-2 select-none" data-id="element-690">
              {sections[activeIndex].label}
            </motion.span>
          </AnimatePresence>

          <button onClick={() => scrollTo(activeIndex + 1)} disabled={activeIndex === sections.length - 1} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Next section" data-id="element-691">
            <ChevronRight size={18} data-id="element-692" />
          </button>
        </div>

        <motion.span key={scrollProgress} initial={{
        opacity: 0.8
      }} animate={{
        opacity: 1
      }} className="bg-[#1A1A1A] text-white text-xs font-semibold tabular-nums rounded-full px-3 select-none shrink-0 w-[46px] text-center shadow-lg shadow-black/10 h-12 flex items-center justify-center" data-id="element-693">
          {scrollProgress}%
        </motion.span>
      </motion.div>
    </div>;
}